/**
 * /ko/decision-log — 의사결정 기록 concept page (Korean). Wedge concept.
 * Links to /ko/voice-to-text bridge.
 */
import { MarketingShell } from './MarketingShell';

export function KoDecisionLog() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/decision-log"
      hreflangLocales={['ko']}
      documentTitle="의사결정 기록(Decision Log)이란｜낡지 않는 운영법 · Draftlize"
      documentDescription="'왜 그렇게 결정했는가'를 남기는 의사결정 기록. 대부분은 쓰고 방치돼 낡습니다. 결정을 카드로 만들고, 바뀌면 의존하는 부분이 자동으로 stale 표시되는 '살아있는' 의사결정 기록."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · 의사결정 기록</span>
          <h1 className="serif" style={{ fontSize: 'clamp(31px, 4.2vw, 52px)', lineHeight: 1.1, margin: '12px 0 24px' }}>
            의사결정 기록——<br />
            <em>"우리가 왜 이렇게 결정했지?"에 답한다.</em>
          </h1>
          <p className="lede">
            의사결정 기록(decision log)은 제품의 모든 결정에 대해 "<em>왜</em> 그렇게 정했는가"를 남기는 장부입니다. 몇 달 뒤 누군가 같은 논의를 다시 꺼낼 때, 당시의 맥락·선택지·트레이드오프가 있으면 곧바로 정리됩니다. 문제는 대부분의 의사결정 기록이 <em>낡는다</em>는 것——한 번 쓰고, 다시 읽히지 않고, 조용히 과거가 됩니다. 이 페이지는 작성법과, 낡지 않게 하는 '살아있는' 운영법을 다룹니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">의사결정 기록에 무엇을 남기나.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>결정</strong>——무엇을 정했는지 한 문장으로.</li>
            <li><strong>맥락</strong>——왜 지금 이 결정이 필요했는지.</li>
            <li><strong>선택지와 트레이드오프</strong>——무엇을 검토하고 왜 다른 것을 버렸는지. <em>나중에 가장 쓸모 있는 부분</em>.</li>
            <li><strong>담당자와 날짜</strong>——누가, 언제.</li>
            <li><strong>영향 범위</strong>——어떤 스펙·결정과 얽혀 있는지.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            이 형식은 ADR(아키텍처 의사결정 기록)과도 통합니다. 결정은 대개 회의에서 나옵니다——먼저 <a href="/ko/voice-to-text" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>음성 텍스트 변환</a>으로 회의에서 결정을 뽑아내는 것이 출발점입니다.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">왜 의사결정 기록은 낡는가. <em>그리고 낡지 않게 하는 법.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            평문 의사결정 기록은 쓰는 순간부터 현실과 벌어집니다. 뒤의 결정이 앞의 결정을 뒤집어도 문서는 조용히 옛 상태로 남습니다. Draftlize에서는 결정이 '살아있는 카드'가 됩니다.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">결정은 참조 가능한 카드</h3>
            <p>결정 하나 = 카드 하나. 맥락·선택지·이유·담당자를 담고 ID로 참조됩니다. 문서 어딘가에 묻히지 않고 언제든 가리킬 수 있습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">뒤집힌 결정은 자동 stale</h3>
            <p>새 결정이 옛 결정을 뒤집으면 연결된 옛 결정과 그 위의 스펙이 자동으로 stale 표시됩니다. 이미 정한 것을 다시 논쟁하지 않습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 에이전트가 읽고 쓴다</h3>
            <p>의사결정 기록이 Claude Code·Cursor가 MCP로 읽는 컨텍스트가 됩니다. 에이전트는 "왜 그렇게 정했는지"를 알고 다음을 씁니다.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          대부분의 의사결정 기록은 쓴 다음 날부터 아무도 읽지 않는다. 그래서 반년 뒤, 같은 논쟁을 처음부터 다시 한다.
        </blockquote>
        <cite>결정을 맥락째 남기고, 바뀌면 영향 범위를 자동으로 알린다. 그게 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '의사결정 기록과 회의록의 차이는?', a: '회의록은 "회의의 기록", 의사결정 기록은 "결정의 장부"입니다. 회의록에서 결정만 뽑아 쌓은 것이 의사결정 기록이며, 나중에 진짜 쓸모 있는 건 후자입니다.' },
            { q: '누가 관리해야 하나요?', a: '보통 그 결정을 책임지는 사람(PM·리드)이 관리합니다. 핵심은 결정의 "왜"를 나중에 되짚을 수 있게 유지하는 것입니다.' },
            { q: '얼마나 자세히 써야 하나요?', a: '결정·맥락·트레이드오프가 드러나면 충분합니다. 길이보다, 뒤집혔을 때 알아챌 수 있는 구조가 더 중요합니다.' },
            { q: 'ADR과 같은가요?', a: 'ADR은 기술 의사결정 기록의 한 종류입니다. Draftlize는 ADR과 제품 결정을 같은 "살아있는 카드"로 다룹니다.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">낡지 않는 의사결정 기록을 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            결정을 맥락째 카드로 만들고, 뒤집히면 자동 stale, AI 에이전트도 읽습니다. Draftlize로 낡지 않는 의사결정 기록을.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
